import { useState, useEffect, useMemo, memo } from 'react';
import {
  Box,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import dayjs from 'dayjs';
import useLasertagGame from '../../../hooks/useLasertagGame';
import useAutoAnnouncements from '../../../hooks/useAutoAnnouncements';
import useLocationStatus from '../../../hooks/useLocationStatus';
import { getSlotColorById, getSlotLabelById, normalizeSlotId } from '../../../util/operatorUtils';

const GAME_DURATION_MIN = 9;

function LaserZoneGameStatus({ entertainment }) {
  const theme = useTheme();
  const { getLasertagGamesForOperator } = useLasertagGame();
  const { config: autoAnnouncementsConfig } = useAutoAnnouncements(entertainment);
  const { sendCommand, audioStatus } = useLocationStatus(entertainment);
  const [activeGame, setActiveGame] = useState(null);
  const [gameStatusTick, setGameStatusTick] = useState(0);

  useEffect(() => {
    if (!entertainment) {
      setActiveGame(null);
      return;
    }

    const fetchGames = async () => {
      try {
        const now = dayjs();
        const fifteenMinutesAgo = now.subtract(15, 'minute');

        const activeGames = await getLasertagGamesForOperator({
          entertainmentId: entertainment,
          status: 'Active',
        });

        if (activeGames && activeGames.length > 0) {
          setActiveGame(activeGames[0]);
          return;
        }

        const sinceDate = fifteenMinutesAgo.toISOString();
        const successGames = await getLasertagGamesForOperator({
          entertainmentId: entertainment,
          status: 'Success',
          since: sinceDate,
        });

        if (successGames && successGames.length > 0) {
          const recentlyEnded = successGames
            .filter(g => g.endTime)
            .sort((a, b) => new Date(b.endTime) - new Date(a.endTime))[0];

          if (recentlyEnded) {
            const endTime = dayjs(recentlyEnded.endTime);
            if (endTime.isAfter(fifteenMinutesAgo)) {
              setActiveGame(recentlyEnded);
              return;
            }
          }
        }

        setActiveGame(null);
      } catch (err) {
        console.error('Error fetching lasertag games:', err);
        setActiveGame(null);
      }
    };

    fetchGames();
    const fetchInterval = setInterval(fetchGames, 30000);
    const tickInterval = setInterval(() => {
      setGameStatusTick(prev => prev + 1);
    }, 1000);

    return () => {
      clearInterval(fetchInterval);
      clearInterval(tickInterval);
    };
  }, [entertainment]);

  const gameStatusData = useMemo(() => {
    if (!activeGame) return null;

    const slotId = normalizeSlotId(
      typeof activeGame.slotId === 'number' ? activeGame.slotId : activeGame.slot
    );
    const color = getSlotColorById(slotId, theme);
    const slotLabel = getSlotLabelById(slotId);

    const isActive = activeGame.status === 'Active';
    const now = dayjs();
    const startedAt = activeGame.startTime ? dayjs(activeGame.startTime) : null;
    const endsAt = activeGame.predictedEndTime
      ? dayjs(activeGame.predictedEndTime)
      : startedAt?.add(GAME_DURATION_MIN, 'minute');
    const endTime = activeGame.endTime ? dayjs(activeGame.endTime) : null;

    let remainingMinutes = null;
    let completionPercent = null;
    let endedSecondsAgo = null;

    if (isActive && startedAt && endsAt) {
      try {
        const diff = Math.ceil(endsAt.diff(now, 'minute', true));
        remainingMinutes = Math.max(0, diff);
        const elapsed = now.diff(startedAt, 'minute', true);
        const duration = endsAt.diff(startedAt, 'minute', true);
        completionPercent = Math.min(100, Math.round((elapsed / duration) * 100));
      } catch (_) {
        // Ignore calculation errors from invalid dates
      }
    } else if (!isActive && endTime) {
      try {
        endedSecondsAgo = Math.max(0, now.diff(endTime, 'second'));
      } catch (_) {
        // Ignore calculation errors from invalid dates
      }
    }

    return {
      isActive,
      slotId,
      color,
      slotLabel,
      remainingMinutes,
      completionPercent,
      endedSecondsAgo,
    };
  }, [activeGame, theme, gameStatusTick]);

  const getAnnouncementFile = (targetSlotId, type) => {
    if (targetSlotId === 0) {
      return type === 'briefing' ? 'briefing-orange.mp3' : 'game-start-orange.mp3';
    } else if (targetSlotId === 1) {
      return type === 'briefing' ? 'briefing-gruen.mp3' : 'game-start-gruen.mp3';
    } else {
      return type === 'briefing' ? 'briefing-now.mp3' : 'game-start-3min.mp3';
    }
  };

  const handlePlayAnnouncement = async (targetSlotId, type) => {
    const fileName = getAnnouncementFile(targetSlotId, type);
    if (fileName) {
      await sendCommand('start', fileName, 'audio');
    }
  };

  const autoAnnouncementsDisabled =
    (!autoAnnouncementsConfig?.briefing?.enabled && !autoAnnouncementsConfig?.gameStart?.enabled) ||
    !autoAnnouncementsConfig;

  if (!gameStatusData || !activeGame) return null;

  return (
    <Paper
      sx={{
        marginBottom: 1.5,
        padding: 1.5,
        flexShrink: 0,
        backgroundColor: gameStatusData.color,
        color: 'white',
      }}
      elevation={3}
    >
      <Stack direction='row' spacing={2} alignItems='center' justifyContent='space-between'>
        <Stack
          direction='row'
          spacing={3}
          alignItems='center'
          flex={1}
          justifyContent='center'
        >
          <Typography
            variant='body2'
            sx={{
              fontWeight: 600,
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              opacity: 0.9,
              textAlign: 'center',
            }}
          >
            {gameStatusData.slotLabel}
          </Typography>

          {gameStatusData.isActive ? (
            <>
              {gameStatusData.remainingMinutes !== null && (
                <Stack
                  direction='row'
                  spacing={1.5}
                  alignItems='center'
                  justifyContent='center'
                >
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 700,
                      fontSize: '2rem',
                      lineHeight: 1,
                      minWidth: '60px',
                      textAlign: 'center',
                    }}
                  >
                    {gameStatusData.remainingMinutes > 0
                      ? gameStatusData.remainingMinutes
                      : '0'}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      fontSize: '0.75rem',
                      opacity: 0.9,
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                    }}
                  >
                    Min
                  </Typography>
                </Stack>
              )}
              {gameStatusData.completionPercent !== null &&
                gameStatusData.completionPercent < 100 && (
                  <Box sx={{ flex: 1, maxWidth: 300, minWidth: 150 }}>
                    <LinearProgress
                      variant='determinate'
                      value={gameStatusData.completionPercent}
                      sx={{
                        height: 12,
                        borderRadius: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: 'white',
                        },
                      }}
                    />
                  </Box>
                )}
            </>
          ) : (
            gameStatusData.endedSecondsAgo !== null && (
              <Stack
                direction='row'
                spacing={1.5}
                alignItems='center'
                justifyContent='center'
              >
                <Typography
                  variant='h4'
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    lineHeight: 1,
                    minWidth: '80px',
                    textAlign: 'center',
                    fontFamily: 'monospace',
                    color: gameStatusData.endedSecondsAgo >= 300 ? '#ff4444' : 'white',
                  }}
                >
                  {Math.floor(gameStatusData.endedSecondsAgo / 60)}:
                  {String(gameStatusData.endedSecondsAgo % 60).padStart(2, '0')}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    fontSize: '0.95rem',
                    opacity: 0.9,
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                    color: gameStatusData.endedSecondsAgo >= 300 ? '#ff4444' : 'white',
                  }}
                >
                  beendet
                </Typography>
              </Stack>
            )
          )}
        </Stack>

        {autoAnnouncementsDisabled && (
          <Stack direction='row' spacing={1.5} alignItems='center'>
            {gameStatusData.slotId === 0 && (
              <>
                <IconButton
                  size='small'
                  disabled={audioStatus?.status !== 'online'}
                  onClick={() => handlePlayAnnouncement(1, 'briefing')}
                  sx={{
                    color: getSlotColorById(1, theme),
                    backgroundColor: `${getSlotColorById(1, theme)}20`,
                    border: `1px solid ${getSlotColorById(1, theme)}`,
                    '&:hover': {
                      backgroundColor: `${getSlotColorById(1, theme)}30`,
                    },
                    '&.Mui-disabled': {
                      color: `${getSlotColorById(1, theme)}60`,
                      borderColor: `${getSlotColorById(1, theme)}40`,
                      backgroundColor: `${getSlotColorById(1, theme)}10`,
                    },
                  }}
                  title='Briefing Grün'
                >
                  <RecordVoiceOverIcon fontSize='small' />
                </IconButton>
                <IconButton
                  size='small'
                  disabled={audioStatus?.status !== 'online'}
                  onClick={() => handlePlayAnnouncement(1, 'gameStart')}
                  sx={{
                    color: getSlotColorById(1, theme),
                    backgroundColor: `${getSlotColorById(1, theme)}20`,
                    border: `1px solid ${getSlotColorById(1, theme)}`,
                    '&:hover': {
                      backgroundColor: `${getSlotColorById(1, theme)}30`,
                    },
                    '&.Mui-disabled': {
                      color: `${getSlotColorById(1, theme)}60`,
                      borderColor: `${getSlotColorById(1, theme)}40`,
                      backgroundColor: `${getSlotColorById(1, theme)}10`,
                    },
                  }}
                  title='Start Grün'
                >
                  <PlayArrowIcon fontSize='small' />
                </IconButton>
              </>
            )}
            {gameStatusData.slotId === 1 && (
              <>
                <IconButton
                  size='small'
                  disabled={audioStatus?.status !== 'online'}
                  onClick={() => handlePlayAnnouncement(0, 'briefing')}
                  sx={{
                    color: getSlotColorById(0, theme),
                    backgroundColor: `${getSlotColorById(0, theme)}20`,
                    border: `1px solid ${getSlotColorById(0, theme)}`,
                    '&:hover': {
                      backgroundColor: `${getSlotColorById(0, theme)}30`,
                    },
                    '&.Mui-disabled': {
                      color: `${getSlotColorById(0, theme)}60`,
                      borderColor: `${getSlotColorById(0, theme)}40`,
                      backgroundColor: `${getSlotColorById(0, theme)}10`,
                    },
                  }}
                  title='Briefing Orange'
                >
                  <RecordVoiceOverIcon fontSize='small' />
                </IconButton>
                <IconButton
                  size='small'
                  disabled={audioStatus?.status !== 'online'}
                  onClick={() => handlePlayAnnouncement(0, 'gameStart')}
                  sx={{
                    color: getSlotColorById(0, theme),
                    backgroundColor: `${getSlotColorById(0, theme)}20`,
                    border: `1px solid ${getSlotColorById(0, theme)}`,
                    '&:hover': {
                      backgroundColor: `${getSlotColorById(0, theme)}30`,
                    },
                    '&.Mui-disabled': {
                      color: `${getSlotColorById(0, theme)}60`,
                      borderColor: `${getSlotColorById(0, theme)}40`,
                      backgroundColor: `${getSlotColorById(0, theme)}10`,
                    },
                  }}
                  title='Start Orange'
                >
                  <PlayArrowIcon fontSize='small' />
                </IconButton>
              </>
            )}
            {gameStatusData.slotId === 2 && (
              <>
                <IconButton
                  size='small'
                  disabled={audioStatus?.status !== 'online'}
                  onClick={() => handlePlayAnnouncement(0, 'briefing')}
                  sx={{
                    color: getSlotColorById(0, theme),
                    backgroundColor: `${getSlotColorById(0, theme)}20`,
                    border: `1px solid ${getSlotColorById(0, theme)}`,
                    '&:hover': {
                      backgroundColor: `${getSlotColorById(0, theme)}30`,
                    },
                    '&.Mui-disabled': {
                      color: `${getSlotColorById(0, theme)}60`,
                      borderColor: `${getSlotColorById(0, theme)}40`,
                      backgroundColor: `${getSlotColorById(0, theme)}10`,
                    },
                  }}
                  title='Briefing Orange'
                >
                  <RecordVoiceOverIcon fontSize='small' />
                </IconButton>
                <IconButton
                  size='small'
                  disabled={audioStatus?.status !== 'online'}
                  onClick={() => handlePlayAnnouncement(1, 'briefing')}
                  sx={{
                    color: getSlotColorById(1, theme),
                    backgroundColor: `${getSlotColorById(1, theme)}20`,
                    border: `1px solid ${getSlotColorById(1, theme)}`,
                    '&:hover': {
                      backgroundColor: `${getSlotColorById(1, theme)}30`,
                    },
                    '&.Mui-disabled': {
                      color: `${getSlotColorById(1, theme)}60`,
                      borderColor: `${getSlotColorById(1, theme)}40`,
                      backgroundColor: `${getSlotColorById(1, theme)}10`,
                    },
                  }}
                  title='Briefing Grün'
                >
                  <RecordVoiceOverIcon fontSize='small' />
                </IconButton>
                <IconButton
                  size='small'
                  disabled={audioStatus?.status !== 'online'}
                  onClick={() => handlePlayAnnouncement(2, 'briefing')}
                  sx={{
                    color: '#fff',
                    backgroundColor: `${getSlotColorById(2, theme)}20`,
                    border: `1px solid #fff`,
                    '&:hover': {
                      backgroundColor: `${getSlotColorById(2, theme)}30`,
                    },
                    '&.Mui-disabled': {
                      color: `#aaa`,
                      borderColor: `#aaa`,
                      backgroundColor: `${getSlotColorById(2, theme)}10`,
                    },
                  }}
                  title='Briefing Alle'
                >
                  <RecordVoiceOverIcon fontSize='small' />
                </IconButton>
                <IconButton
                  size='small'
                  disabled={audioStatus?.status !== 'online'}
                  onClick={() => handlePlayAnnouncement(0, 'gameStart')}
                  sx={{
                    color: getSlotColorById(0, theme),
                    backgroundColor: `${getSlotColorById(0, theme)}20`,
                    border: `1px solid ${getSlotColorById(0, theme)}`,
                    '&:hover': {
                      backgroundColor: `${getSlotColorById(0, theme)}30`,
                    },
                    '&.Mui-disabled': {
                      color: `${getSlotColorById(0, theme)}60`,
                      borderColor: `${getSlotColorById(0, theme)}40`,
                      backgroundColor: `${getSlotColorById(0, theme)}10`,
                    },
                  }}
                  title='Start Orange'
                >
                  <PlayArrowIcon fontSize='small' />
                </IconButton>
                <IconButton
                  size='small'
                  disabled={audioStatus?.status !== 'online'}
                  onClick={() => handlePlayAnnouncement(1, 'gameStart')}
                  sx={{
                    color: getSlotColorById(1, theme),
                    backgroundColor: `${getSlotColorById(1, theme)}20`,
                    border: `1px solid ${getSlotColorById(1, theme)}`,
                    '&:hover': {
                      backgroundColor: `${getSlotColorById(1, theme)}30`,
                    },
                    '&.Mui-disabled': {
                      color: `${getSlotColorById(1, theme)}60`,
                      borderColor: `${getSlotColorById(1, theme)}40`,
                      backgroundColor: `${getSlotColorById(1, theme)}10`,
                    },
                  }}
                  title='Start Grün'
                >
                  <PlayArrowIcon fontSize='small' />
                </IconButton>
                <IconButton
                  size='small'
                  disabled={audioStatus?.status !== 'online'}
                  onClick={() => handlePlayAnnouncement(2, 'gameStart')}
                  sx={{
                    color: '#fff',
                    backgroundColor: `${getSlotColorById(2, theme)}20`,
                    border: `1px solid #fff`,
                    '&:hover': {
                      backgroundColor: `${getSlotColorById(2, theme)}30`,
                    },
                    '&.Mui-disabled': {
                      color: `#aaa`,
                      borderColor: `#aaa`,
                      backgroundColor: `${getSlotColorById(2, theme)}10`,
                    },
                  }}
                  title='Start Alle'
                >
                  <PlayArrowIcon fontSize='small' />
                </IconButton>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

export default memo(LaserZoneGameStatus);
